import { React, useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import { useLanguage } from '../../Context/LanguageContext';
import { ReactComponent as Menu2IconSvg } from '../../image/Menu2.svg';
import { ReactComponent as LockIconSvg } from '../../image/Lock.svg';
import CalcDate from '../../Model/Share/Normal/CalcDate';
import moment from 'moment';

const LockerInfo = forwardRef((props, ref) => {
  const {
    index,
    lockerIndex,
    data,
    clickFunctionFn,
    clickUsageHistoryFn,
    clickChangeStateFn,
    clickDeleteLockerFn,
    clickChangeNameFn,
    clickSendMessageFn,
    clickExtendFn
  } = props;
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [clickedMore, setClickedMore] = useState(false);
  const [individualData, setIndividualData] = useState();

  const MoreButtonRef = useRef();
  const MoreBoxRef = useRef();
  const HistoryRef = useRef();
  const TextRef = useRef();
  const LockRef = useRef();
  const DeleteRef = useRef();
  const ChangeRef = useRef();
  const MessageRef = useRef();
  const ExtendRef = useRef();

  const history = useHistory();

  useImperativeHandle(ref, () => ({
    setClickedMore: (val) => {
      setClickedMore(val);
    },
    getMoreButtonRef: () => {
      return MoreButtonRef.current;
    },
    getMoreBoxRef: () => {
      return MoreBoxRef.current;
    }
  }));

  useEffect(() => {
    if (data.state === 'Empty') {
      data.isAvailable = true;
      data.isUsed = false;
      data.stateCode = 'Empty';
      data.state = t('main.locker.empty');
      data.stateColor = theme.text;
      data.days = '';
      data.btnText = t('main.locker.startUsage');
      data.border = `1px solid ${theme.line}`;
    }
    else if (data.state === 'Using') {
      const isExpired = !moment(data.endDate).isAfter(moment()) && moment(data.endDate).format('YYYY.MM.DD') !== moment().format('YYYY.MM.DD');
      data.isAvailable = true;
      data.isUsed = true;
      data.isOverused = isExpired;
      data.stateCode = 'Using';
      data.state = isExpired ? t('main.locker.overused') : t('main.locker.inUse');
      data.stateColor = isExpired ? theme.pointRed : theme.subKey;
      data.days = CalcDate.getDday(moment(data.endDate).format('YYYY-MM-DDT00:00:00'));
      data.btnText = t('main.locker.endUsage');
      data.border = isExpired ? `2px solid ${theme.pointRed}` : `2px solid ${theme.subKey}`;
    }
    else if (data.state === 'Invalid') {
      data.isAvailable = false;
      data.isUsed = false;
      data.stateCode = 'Invalid';
      data.state = t('main.locker.unavailable');
      data.stateColor = theme.pointRed;
      data.days = '';
      data.btnText = t('main.locker.unlock');
      data.border = `1px solid ${theme.line}`;
    }
    else if (data.state === 'Expected') {
      data.isAvailable = false;
      data.isUsed = false;
      data.stateCode = 'Expected';
      data.state = t('main.locker.expected');
      data.stateColor = theme.text;
      data.days = '-';
      data.btnText = t('main.locker.unlock');
      data.border = `1px solid ${theme.line}`;
    }

    setIndividualData(data);
  }, [data, theme])

  useEffect(() => {
    if (clickedMore) {
      MoreBoxRef.current.style.visibility = 'visible';
      MoreBoxRef.current.style.border = `1px solid ${theme.primary}`;
      MoreBoxRef.current.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
      if (data.stateCode === 'Empty') MoreBoxRef.current.style.height = `180px`;
      else if (data.stateCode === 'Using') {
        MoreBoxRef.current.style.height = `144px`;
      }
      else MoreBoxRef.current.style.height = `108px`;
    }
    else {
      MoreBoxRef.current.style.visibility = 'hidden';
      MoreBoxRef.current.style.border = `0px solid ${theme.primary}`;
      MoreBoxRef.current.style.boxShadow = 'none';
      MoreBoxRef.current.style.height = `0px`;
    }
  }, [clickedMore, data, theme])

  // 마우스 클릭 감지 리스너 //
  useEffect(() => {
    document.addEventListener("mousedown", onClickOutSide);

    return () => {
      document.removeEventListener("mousedown", onClickOutSide);
    }
  }, [])

  // 마우스 커서가 락커가 아닌곳을 클릭할 경우 체크 //
  function onClickOutSide(e) {
    // MoreButtonRef와 MoreBoxRef는 contains로 자식 요소 클릭도 체크
    if (MoreBoxRef.current && !MoreBoxRef.current.contains(e.target)
      && MoreButtonRef.current && !MoreButtonRef.current.contains(e.target)
      && HistoryRef.current !== e.target
      && TextRef.current !== e.target
      && LockRef.current !== e.target
      && DeleteRef.current !== e.target
      && ChangeRef.current !== e.target
      && MessageRef.current !== e.target
      && ExtendRef.current !== e.target) {
      setClickedMore(false);
    }
  }

  // 더보기 버튼 클릭시 //
  function MoreButtonClick() {
    if (data.stateCode === 'Expected') {
      alert(t('main.locker.paymentRequired'));
      return;
    }

    // 메뉴가 닫혀있을 때만 열기 (이미 열려있으면 아무것도 하지 않음)
    if (!clickedMore) {
      setClickedMore(true);
    }
  }

  // 사용자 이름 및 D-day클릭시 //
  function onClickMidLineWrapper() {
    if (data.memberId === 0) return;

    window.open('/member/valid/detail/' + data.memberId + '/locker', '_blank');
  }

  // D-Day 검사 //
  function checkDday() {
    if (data.days === '') return '';
    else if (data.days === '-') return 'Expected';
    else if (moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'day') > 0) return 'D-' + moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'day');
    else if (moment(data.endDate).format('YYYY.MM.DD') === moment().format('YYYY.MM.DD')) return 'D-Day'
    else return 'D+' + moment(moment().format('YYYY-MM-DD')).diff(moment(moment(data.endDate).format('YYYY-MM-DD')), 'day')
  }

  return (
    <TopLevelWrapper>
      <InfoBox border={data.border}>
        <TopLineWrapper>
          <Number>{data.lockerName}</Number>
          <Menu2Icon ref={MoreButtonRef} onClick={() => MoreButtonClick()} />
        </TopLineWrapper>
        <MidLineWrapper onClick={onClickMidLineWrapper}>
          <NameWrapper>
            <Name>{data.userName}</Name>
          </NameWrapper>
          <Dday>{checkDday()}</Dday>
        </MidLineWrapper>
        <BottomLineWrapper>
          {
            data.stateCode === 'Invalid' &&
            <LockIcon />
          }
          <State stateColor={data.stateColor}>{data.state}</State>
        </BottomLineWrapper>
      </InfoBox>
      <MoreBox ref={MoreBoxRef}>
        <MoreButton ref={HistoryRef} onClick={() => clickUsageHistoryFn(index, lockerIndex)}>{t('main.locker.usageHistory')}</MoreButton>
        <MoreButton ref={TextRef} onClick={() => clickFunctionFn(index, lockerIndex)}>{data.btnText}</MoreButton>
        {
          data.stateCode === 'Empty' &&
          <MoreButton ref={LockRef} onClick={() => clickChangeStateFn(index, lockerIndex)}>{t('main.locker.lockLocker')}</MoreButton>
        }
        {
          data.stateCode === 'Empty' &&
          <MoreButton ref={DeleteRef} onClick={() => clickDeleteLockerFn(index, lockerIndex)}>{t('main.locker.deleteLocker')}</MoreButton>
        }
        <MoreButton ref={ChangeRef} onClick={() => clickChangeNameFn(index, lockerIndex)}>{t('main.locker.changeName')}</MoreButton>
        {
          data.stateCode === 'Using' &&
          <MoreButton ref={ExtendRef} onClick={() => clickExtendFn(index, lockerIndex)}>{t('main.locker.extendUsage')}</MoreButton>
        }
      </MoreBox>
    </TopLevelWrapper>
  )
});

export default LockerInfo;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  padding: 10px;

  box-sizing: border-box;
  width: 122px;
  height: 122px;

  border: ${props => props.border};
  border-radius: 12px;

  transition: all 0.3s ease-in-out;
`;

const TopLineWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
  height: 15px;

  overflow: hidden;
`;

const Number = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;

  @media (hover: hover) {
    &:hover {
      cursor: default;
    }
  }
`;

const Menu2Icon = styled(Menu2IconSvg)`
  width: 4px;
  height: 13px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.subText};
    transition: fill 0.3s ease-in-out;
  }

  @media (hover: hover) {
    &:hover {
      cursor: pointer;

      & path {
        fill: ${props => props.theme.primary};
      }
    }
  }
`;

const MidLineWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  margin-top: 10px;

  width: 100%;
  height: 46px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const NameWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
  height: 20px;

  overflow: hidden;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const Name = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
`;

const Dday = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
`;

const BottomLineWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  margin-top: 10px;

  width: 100%;
`;

const LockIcon = styled(LockIconSvg)`
  width: 11px;
  height: 14px;

  flex-shrink: 0;
  margin-right: 4px;

  & path {
    fill: ${props => props.theme.pointRed};
  }
`;

const State = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.stateColor};

  margin: 0 0 0 0;

  @media (hover: hover) {
    &:hover {
      cursor: default;
    }
  }
`;

const MoreBox = styled.div`
  position: absolute;
  z-index: 10;

  display: flex;
  flex-direction: column;
  overflow: hidden;

  justify-content: center;
  align-items: center;

  width: 78px;
  height: 0px;

  margin-top: 35px;
  margin-left: 60px;

  border-radius: 8px;
  background-color: ${props => props.theme.subBackground};
  box-shadow: none;

  visibility: hidden;

  transition: all 0.3s ease-in-out;
`;

const MoreButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 78px;
  height: 36px;

  background-color: ${props => props.theme.subBackground};

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.primary};

  margin: 0 0 0 0;

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.theme.primary};
      color: ${props => props.theme.white};
    }
  }
`;
